import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {TermsOfAgreementsAnswer} from "../../models/terms-of-agreements-answers.model";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {TermsOfAgreementService} from "../../providers/terms-of-agreement.service";
import {CommonUtils} from "../../utils/common";
import {AppStateService} from "../../providers/app-state.service";
import {TermsOfAgreementCustomParam} from "../../models/terms-of-agreement.model";
import {InputChangeEventDetail, ItemReorderEventDetail} from "@ionic/angular";

@Component({
  selector: 'app-generate-terms',
  templateUrl: './generate-terms.component.html',
  styleUrls: ['./generate-terms.component.scss'],
})
export class GenerateTermsComponent implements OnInit {

  @Input()
  termOfAgreementAnswers: TermsOfAgreementsAnswer;

  @Input()
  termsOfAgreementCustomParams: Map<string, TermsOfAgreementCustomParam[]>;

  clinicName: string;
  termsOfAgreementAnswersFormGroup: FormGroup;
  @Input()
  disableQuestions: boolean;
  isBusy: boolean;

  @Output()
  generateTermOfAgreementEmitter: EventEmitter<{ parameters: TermsOfAgreementsAnswer, customParams: Map<string, TermsOfAgreementCustomParam[]> }> = new EventEmitter<{ parameters: TermsOfAgreementsAnswer, customParams: Map<string, TermsOfAgreementCustomParam[]> }>();

  @Output()
  cancelGenerateTermOfAgreementEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  hasUnsavedChanges: boolean;
  customParamsActive: boolean = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.termOfAgreementAnswers && changes.termOfAgreementAnswers?.currentValue) {
      this.setupFields();
    }

    if (changes && changes.termOfAgreementAnswers && changes.termsOfAgreementCustomParams?.currentValue) {
      this.setupFields();
    }
  }

  constructor(private termsOfAgreementService: TermsOfAgreementService,
              private formBuilder: FormBuilder, public commonUtils : CommonUtils,
              private appStateService: AppStateService) {
  }

  ngOnInit() {
    this.appStateService.isBusySubject.subscribe(e => {
      this.isBusy = e;
    });

    this.setupFields();
  }

  submitTerms() {
    this.appStateService.updateIsBusySubject(true);
    this.commonUtils.presentYesNoActionSheet('Na pewno chcesz zastosować powyższe zmiany w parametrach?',
      'Tak', 'Nie', 'checkmark', 'close', () => {
        this.termOfAgreementAnswers = this.commonUtils.formGroupValuesToObject<TermsOfAgreementsAnswer>(new TermsOfAgreementsAnswer(), this.termsOfAgreementAnswersFormGroup);

        const emitValue: { parameters: TermsOfAgreementsAnswer, customParams: Map<string, TermsOfAgreementCustomParam[]> } = { parameters: this.termOfAgreementAnswers, customParams: this.termsOfAgreementCustomParams }
        this.generateTermOfAgreementEmitter.emit(emitValue);

        this.appStateService.updateIsBusySubject(false);
        this.customParamsActive = false;
      }, () => {
        this.appStateService.updateIsBusySubject(false);
        this.customParamsActive = false;
      });
  }

  markUnsavedChanges() {
    if (!this.hasUnsavedChanges) {
      this.hasUnsavedChanges = true;
    }
  }

  isFormFieldInvalid(group: any, fieldName: string, validationType: string) {
    return this.commonUtils.isFormFieldInvalid(group, fieldName, validationType);
  }

  onCancelAdd() {
    if(this.hasUnsavedChanges) {
      this.appStateService.updateIsBusySubject(true);
      this.commonUtils.presentYesNoActionSheet('Masz nie zapisane zmiany, czy chcesz je anulować?',
        'Tak', 'Nie', 'checkmark', 'close', () => {
          this.cancelGenerateTermOfAgreementEmitter.emit(true);
          this.appStateService.updateIsBusySubject(false);
        }, () => {
          this.appStateService.updateIsBusySubject(false);
        });
    } else{
      this.cancelGenerateTermOfAgreementEmitter.emit(true);
    }
  }

  toggleCustomParamsActive() {
    this.customParamsActive = !this.customParamsActive;
  }

  reorderCustomParameters(number: string, event: CustomEvent<ItemReorderEventDetail>) {
    this.termsOfAgreementCustomParams.set(number, this.commonUtils.immutableArrayMove(this.termsOfAgreementCustomParams.get(number), event.detail.from, event.detail.to));
    this.markUnsavedChanges();
    event.detail.complete();
  }

  onCustomParamChange(number: string, idx: number, event: CustomEvent<InputChangeEventDetail>) {
    if (this.termsOfAgreementCustomParams.has(number) !== undefined) {
      if (this.termsOfAgreementCustomParams.get(number)[idx]) {
        this.termsOfAgreementCustomParams.get(number)[idx].text = event.detail.value;
      }
      this.markUnsavedChanges();
    }
  }

  addNewCustomParam(number: string) {
    if (!this.termsOfAgreementCustomParams.has(number)) {
      this.termsOfAgreementCustomParams.set(number, []);
    }

    this.termsOfAgreementCustomParams.get(number).push(new TermsOfAgreementCustomParam());
  }

  removeCustomParam(number: string, idx: number) {
    if (this.termsOfAgreementCustomParams.has(number)) {
      if (this.termsOfAgreementCustomParams.get(number)[idx] !== undefined) {
        const qadd = (this.termsOfAgreementCustomParams.get(number)[idx]?.text ? ` '${this.termsOfAgreementCustomParams.get(number)[idx].text}'` : '');
        this.commonUtils.presentYesNoActionSheet(`Na pewno chcesz usunąć dodatkowy punkt${qadd}?`, 'Tak', 'Nie', 'trash', 'close', () => {
          this.termsOfAgreementCustomParams.get(number).splice(idx, 1);
          this.markUnsavedChanges();
        }, () => {
        });
      } else {

      }
    }
  }

  private setupFields() {
    if(!this.termOfAgreementAnswers){
      this.termsOfAgreementAnswersFormGroup = this.formBuilder.group({
        clinicName: ['', Validators.required],
        clinicAddress: ['', Validators.required],
        prepaidLockDate: ['', Validators.required],
        maxReservationCancellation: ['', Validators.required],
        complaintEmailAddress: ['', [Validators.required, Validators.email]],
        complaintConsiderationDays: ['', Validators.required],
      });
    } else{
      this.termsOfAgreementAnswersFormGroup = this.formBuilder.group({
        clinicName: [this.termOfAgreementAnswers.clinicName, Validators.required],
        clinicAddress: [this.termOfAgreementAnswers.clinicAddress, Validators.required],
        prepaidLockDate: [this.termOfAgreementAnswers.prepaidLockDate, Validators.required],
        maxReservationCancellation: [this.termOfAgreementAnswers.maxReservationCancellation, Validators.required],
        complaintEmailAddress: [this.termOfAgreementAnswers.complaintEmailAddress, [Validators.required, Validators.email]],
        complaintConsiderationDays: [this.termOfAgreementAnswers.complaintConsiderationDays, Validators.required],
      });
    }
  }

  numericOnly(event): boolean {
    const pattern = /^([0-9])$/;
    const result = pattern.test(event.key);
    return result;
  }

}
